import React, { useState, useRef } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import * as bundleService from "../../../../Products/services/bundleService";

const ProductsSection = ({
  orderProducts,
  job,
  onAddProduct,
  onScanBarcode,
  onRemoveProduct,
  availableProducts,
  productsLoading,
}) => {
  const queryClient = useQueryClient();

  // Inline add form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBundleSelector, setShowBundleSelector] = useState(false);
  const [formData, setFormData] = useState({
    productId: "",
    quantity: "",
    unitPrice: "",
    customName: "",
  });
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddingBundle, setIsAddingBundle] = useState(false);
  const productPickerRef = useRef(null);

  // Fetch bundles
  const { data: bundles = [], isLoading: bundlesLoading } = useQuery({
    queryKey: ["bundles"],
    queryFn: bundleService.getActiveBundles,
  });

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (productPickerRef.current && !productPickerRef.current.contains(event.target)) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Show every product individually (keyed by id). Previously we deduped by name|price,
  // which collapsed duplicate rows but caused the dropdown to save the wrong product id
  // when multiple products shared a name. Duplicates are now disambiguated by SKU/id in
  // the dropdown label.
  const sortedProducts = React.useMemo(() => {
    const safeProducts = Array.isArray(availableProducts) ? availableProducts : [];
    return [...safeProducts].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [availableProducts]);

  const filteredProducts = React.useMemo(() => {
    const term = productSearchTerm.trim().toLowerCase();
    if (!term) return sortedProducts;
    return sortedProducts.filter(p =>
      (p.name || "").toLowerCase().includes(term) ||
      (p.sku || "").toLowerCase().includes(term)
    );
  }, [sortedProducts, productSearchTerm]);

  const handleAddProductClick = () => {
    setShowAddForm(true);
    setShowBundleSelector(false);
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setFormData({
      productId: "",
      quantity: "",
      unitPrice: "",
      customName: "",
      selectedProductName: "",
      selectedProductSku: null,
    });
    setProductSearchTerm("");
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();

    const isNewProduct = formData.productId === "NEW";
    if ((!formData.productId && !isNewProduct) || !formData.quantity) {
      alert("Please select a product and enter quantity");
      return;
    }
    if (isNewProduct && (!formData.customName || !formData.unitPrice)) {
      alert("Please enter product name and unit price for new product");
      return;
    }

    setIsSubmitting(true);

    try {
      let productData = {
        productId: formData.productId && formData.productId !== "NEW" ? parseInt(formData.productId) : null,
        quantity: parseInt(formData.quantity),
        unitPrice: formData.unitPrice ? parseFloat(formData.unitPrice) : null,
        source: "Manual Entry"
      };

      let payload = {
        jobId: job.id,
        material: productData
      };

      // If NEW product, create it first then add
      if (isNewProduct && formData.customName) {
        const productPayload = {
          name: formData.customName,
          unitPrice: formData.unitPrice || 0,
          wholesalePrice: formData.unitPrice || 0,
          description: `Custom product: ${formData.customName}`,
        };
        const productService = await import("../../../services/productService");
        const created = await productService.createProduct(productPayload);
        if (!created || !created.id) throw new Error("Failed to create product");

        queryClient.invalidateQueries({ queryKey: ["products"] });

        payload = {
          ...payload,
          material: {
            ...productData,
            productId: created.id,
          }
        };
      }

      await onAddProduct(payload);
      handleCancelAdd();
    } catch (error) {
      console.error("Failed to add product:", error);
      alert("Failed to add product: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveClick = (product) => {
    onRemoveProduct(product.id);
  };

  // Get display price (prefer wholesale)
  const getDisplayPrice = (product) => {
    return product.wholesalePrice || product.unitPrice || 0;
  };

  // Add all products from a bundle to the order
  const handleAddBundle = async (bundle) => {
    if (!bundle.items || bundle.items.length === 0) {
      alert("This bundle has no items");
      return;
    }

    setIsAddingBundle(true);

    try {
      // Add each product from the bundle
      for (const item of bundle.items) {
        const payload = {
          jobId: job.id,
          material: {
            productId: item.product.id,
            quantity: item.quantity,
            unitPrice: item.product.wholesalePrice || item.product.unitPrice || 0,
            source: `Bundle: ${bundle.name}`
          }
        };
        await onAddProduct(payload);
      }
      setShowBundleSelector(false);
    } catch (error) {
      console.error("Failed to add bundle:", error);
      alert("Failed to add bundle: " + error.message);
    } finally {
      setIsAddingBundle(false);
    }
  };

  // Calculate totals
  const totalQuantity = orderProducts.reduce((sum, p) => sum + (p.quantity || 0), 0);
  const orderTotal = orderProducts.reduce((sum, p) => sum + (p.price || 0) * p.quantity, 0);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-2.5 sm:p-4 mt-2 sm:mt-3">
      <div className="mb-2 sm:mb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
          <h3 className="text-sm sm:text-base font-semibold">Products ({orderProducts.length})</h3>
        </div>

        <div className="flex flex-wrap gap-1.5 sm:gap-2">
          <button
            onClick={handleAddProductClick}
            className="px-2 sm:px-3 py-1 sm:py-1.5 bg-blue-500 text-white rounded text-xs sm:text-sm hover:bg-blue-600 transition-colors"
          >
            + Add Product
          </button>
          {bundles.length > 0 && (
            <button
              onClick={() => {
                setShowBundleSelector(!showBundleSelector);
                setShowAddForm(false);
              }}
              className="px-2 sm:px-3 py-1 sm:py-1.5 bg-purple-500 text-white rounded text-xs sm:text-sm hover:bg-purple-600 transition-colors inline-flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
              </svg>
              Add Bundle
            </button>
          )}
          <button
            onClick={onScanBarcode}
            className="px-2 sm:px-3 py-1 sm:py-1.5 bg-gray-600 text-white rounded text-xs sm:text-sm hover:bg-gray-700 transition-colors"
          >
            Scan Barcode
          </button>
        </div>
      </div>

      {/* Bundle Selector */}
      {showBundleSelector && (
        <div className="bg-purple-50 border-2 border-purple-500 rounded-lg p-3 sm:p-4 mb-2 sm:mb-3 animate-fadeIn">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-semibold text-gray-800 flex items-center gap-2">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
              </svg>
              Select a Bundle to Add
            </h4>
            <button
              onClick={() => setShowBundleSelector(false)}
              className="text-gray-500 hover:text-gray-700 text-xl font-bold"
              type="button"
            >
              ×
            </button>
          </div>

          {bundlesLoading ? (
            <div className="text-center py-4 text-gray-500">Loading bundles...</div>
          ) : bundles.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {bundles.map((bundle) => {
                const totalItems = bundle.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
                const totalPrice = bundle.items?.reduce((sum, item) => {
                  const price = item.product?.wholesalePrice || item.product?.unitPrice || 0;
                  return sum + (price * (item.quantity || 0));
                }, 0) || 0;

                return (
                  <div
                    key={bundle.id}
                    className="bg-white rounded-lg border border-purple-200 p-3 hover:border-purple-400 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">{bundle.name}</div>
                        {bundle.description && (
                          <div className="text-xs text-gray-500 mt-0.5">{bundle.description}</div>
                        )}
                        <div className="text-xs text-gray-600 mt-1">
                          {totalItems} items • ${totalPrice.toFixed(2)} total
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {bundle.items?.slice(0, 3).map((item, idx) => (
                            <span key={idx} className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                              {item.product?.name} x{item.quantity}
                            </span>
                          ))}
                          {bundle.items?.length > 3 && (
                            <span className="text-xs text-gray-500">+{bundle.items.length - 3} more</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleAddBundle(bundle)}
                        disabled={isAddingBundle}
                        className="px-3 py-1.5 bg-purple-500 text-white text-sm rounded hover:bg-purple-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors ml-2"
                      >
                        {isAddingBundle ? "Adding..." : "Add All"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">
              No bundles available. Create bundles in the Products page.
            </div>
          )}
        </div>
      )}

      {/* Inline Add Product Form */}
      {showAddForm && (
        <div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-3 sm:p-4 mb-2 sm:mb-3 animate-fadeIn">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-semibold text-gray-800">Add Product to Order</h4>
            <button
              onClick={handleCancelAdd}
              className="text-gray-500 hover:text-gray-700 text-xl font-bold"
              type="button"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmitAdd} className="space-y-3">
            {/* Product Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product *
              </label>
              <div className="relative" ref={productPickerRef}>
                <input
                  type="text"
                  value={productSearchTerm}
                  onChange={(e) => {
                    setProductSearchTerm(e.target.value);
                    setShowProductDropdown(true);
                  }}
                  onFocus={() => setShowProductDropdown(true)}
                  placeholder="Search products..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={productsLoading}
                />
                {showProductDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-auto">
                    <div
                      className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm text-blue-600 border-b border-gray-200 font-medium"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, productId: "NEW" }));
                        setProductSearchTerm("");
                        setShowProductDropdown(false);
                      }}
                    >
                      + Create new product
                    </div>
                    {productsLoading ? (
                      <div className="px-3 py-2 text-gray-500 text-center text-sm">Loading...</div>
                    ) : filteredProducts.length > 0 ? (
                      filteredProducts.map((product) => (
                        <div
                          key={product.id}
                          className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              productId: String(product.id),
                              selectedProductName: product.name,
                              selectedProductSku: product.sku || null,
                            }));
                            setProductSearchTerm(product.name);
                            setShowProductDropdown(false);
                          }}
                        >
                          <div className="flex justify-between gap-2">
                            <span className="font-medium truncate">{product.name}</span>
                            <span className="text-gray-600 flex-shrink-0">${getDisplayPrice(product).toFixed(2)}</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {product.sku ? `SKU: ${product.sku}` : `ID: ${product.id}`}
                            {product.size ? ` • ${product.size}` : ""}
                            {product.flavor ? ` • ${product.flavor}` : ""}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-gray-500 text-center text-sm">No products found</div>
                    )}
                  </div>
                )}
              </div>
              {formData.productId && (
                <div className="mt-1 text-xs text-green-600">
                  {formData.productId === "NEW"
                    ? "✓ Creating new product"
                    : `✓ Selected: ${formData.selectedProductName || "(unknown)"}${formData.selectedProductSku ? ` — SKU ${formData.selectedProductSku}` : ` — ID ${formData.productId}`}`}
                </div>
              )}
            </div>

            {/* New Product Name (if creating new) */}
            {formData.productId === "NEW" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name *
                </label>
                <input
                  type="text"
                  value={formData.customName}
                  onChange={(e) => setFormData(prev => ({ ...prev, customName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  placeholder="Enter product name"
                />
              </div>
            )}

            {/* Quantity and Unit Price in a row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity *
                </label>
                <input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  min="1"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price {formData.productId === "NEW" && "*"}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.unitPrice}
                  onChange={(e) => setFormData(prev => ({ ...prev, unitPrice: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required={formData.productId === "NEW"}
                  min="0"
                  placeholder="Optional"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {formData.productId === "NEW"
                    ? "Required for new product"
                    : "Leave empty to use wholesale price"}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`flex-1 px-4 py-2 rounded-md text-white font-medium transition-colors ${
                  isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {isSubmitting ? 'Adding...' : '✓ Add Product'}
              </button>
              <button
                type="button"
                onClick={handleCancelAdd}
                disabled={isSubmitting}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {orderProducts.length > 0 ? (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block border border-gray-200 rounded-lg overflow-hidden">
            <div className="max-h-[300px] sm:max-h-[350px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Product</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Unit Price</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Qty</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Line Total</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {orderProducts.map((product, idx) => {
                    const price = product.price || 0;
                    const lineTotal = price * product.quantity;

                    return (
                      <tr key={product.id ?? `${product.name}-${product.price}-${idx}`} className="hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <div>
                            <div className="font-medium text-gray-900 text-sm">{product.name}</div>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900">
                          ${price.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900">
                          {product.quantity}
                        </td>
                        <td className="px-3 py-2 text-sm font-medium text-gray-900">
                          ${lineTotal.toFixed(2)}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => handleRemoveClick(product)}
                            className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600 transition-colors"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-2">
            {orderProducts.map((product, idx) => {
              const price = product.price || 0;
              const lineTotal = price * product.quantity;

              return (
                <div
                  key={product.id ?? `${product.name}-${product.price}-${idx}`}
                  className="bg-gray-50 p-3 rounded-lg border border-gray-200"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 pr-2">
                      <div className="font-semibold text-gray-900 text-sm break-words">{product.name}</div>
                      <div className="text-xs text-gray-600 mt-1">${price.toFixed(2)} each</div>
                    </div>
                    <button
                      onClick={() => handleRemoveClick(product)}
                      className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600 transition-colors flex-shrink-0"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600">Qty:</span>
                      <span className="text-sm font-medium text-gray-900">{product.quantity}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-600">Total</div>
                      <div className="font-bold text-sm text-gray-900">${lineTotal.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Order Summary */}
          {orderProducts.length > 0 && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex justify-between text-sm font-bold">
                <span className="text-gray-900">Order Total ({totalQuantity} items):</span>
                <span className="text-gray-900">${orderTotal.toFixed(2)}</span>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-4 sm:py-6 bg-gray-50 rounded-lg">
          <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-1">No products added yet</h3>
          <p className="text-xs sm:text-sm text-gray-500">Use the buttons above to add products to this order</p>
          {bundles.length > 0 && (
            <button
              onClick={() => setShowBundleSelector(true)}
              className="mt-3 px-4 py-2 bg-purple-500 text-white rounded-md text-sm hover:bg-purple-600 transition-colors inline-flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
              </svg>
              Quick Add from Bundle
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductsSection;
